import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  ProjectIdentity,
  RootStateTypeExtra,
  ThemeConfigInterface,
} from '@store/types'

import Container from '@components/container/v2'
import DataHandler from '@components/data-handler'
import { Status } from '@components/data-handler/data-handler'
import FILTER_INITIAL_STATE from '@components/filter/filterState'
import ImageHandler from '@components/image-handler'
import ProjectSwitchToggle from '@components/project-switch/ProjectSwitchToggle'
import { CanvasInteractive } from '@components/showcase-canvas'
import { CanvasRefInterface } from '@components/showcase-canvas/canvas-interactive'
import { MarkerType } from '@components/showcase-canvas/constants'
import { Polygon } from '@components/showcase-canvas/types'

import {
  BuildingInterface,
  selectFromResult as selectFromBuildingResult,
  useGetBuildingQuery,
} from '@api/building'
import {
  InteractivePlanData,
  MapContent,
  selectFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'

import styleUtil, { BaseThemeConfig } from '@utilities/style-util'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AreaViewApartmentsSkeleton from './area-view-skeleton'

export interface AreaViewProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
}

const ARROW_TYPE = 'arrow'
const ARROW_SMALL_TYPE = 'arrow-small'

function AreaViewApartments({ session, projectIdentity }: AreaViewProps) {
  const location = useLocation()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const themeData: BaseThemeConfig = styleUtil()

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<CanvasRefInterface>()

  const [isCanvasImageLoaded, setImageLoaded] = useState<boolean>(false)

  const [interactivePlan, setInteractivePlan] = useState<InteractivePlanData>({
    areaView: {
      image: '',
      polygons: [],
    },
    blocks: {},
    floorplan: {},
    precinct: {},
    stages: {},
  })

  const [building, setBuilding] = useState<BuildingInterface>({
    levels: [],
    blockOrders: [],
  })

  const [theme, setTheme] = useState<
    Pick<ThemeConfigInterface, 'font' | 'mainColour'>
  >({
    font: '',
    mainColour: '',
  })

  const interactivePlanQueryResult = useGetInteractivePlanQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult }
  )

  const buildingQueryResult = useGetBuildingQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromBuildingResult }
  )

  const apiStatus = useMemo(() => {
    const { FULFILLED, REJECTED, PENDING } = Status
    const { status: interactiveQueryResultStatus } = interactivePlanQueryResult
    const { status: buildingQueryResultStatus } = buildingQueryResult

    if (
      interactiveQueryResultStatus === FULFILLED &&
      buildingQueryResultStatus === FULFILLED
    ) {
      return FULFILLED
    }

    if (
      interactiveQueryResultStatus === REJECTED ||
      buildingQueryResultStatus === REJECTED
    ) {
      return REJECTED
    }

    return PENDING
  }, [interactivePlanQueryResult.status, buildingQueryResult.status])

  const updateTriggerBuilding = (state: boolean) =>
    firebaseControlQuery.update({
      [`areaView.triggerBuilding`]: state,
    })

  const resetBuildingMap = () =>
    firebaseControlQuery.update({
      [`building.activeBlock`]: '',
      [`building.activeLevel`]: '',
      [`building.activeUnit`]: '',
      [`building.unitGalleryIndex`]: 0,
    })

  const resetBuildingView = () =>
    firebaseControlQuery.update({
      [`building.sidePanelFolded`]: true,
      [`building.unitFilter`]: FILTER_INITIAL_STATE,
    })

  const getLabel = (poly: Polygon) => {
    const polyType = poly.type || ''
    if (
      polyType === ARROW_TYPE ||
      polyType === ARROW_SMALL_TYPE ||
      polyType === MarkerType.oblongLabel ||
      polyType === MarkerType.oblongStatus
    ) {
      return poly.label || poly.groupId
    }
    return poly.label
  }

  const setInteractiveAction = (areaViewMap: MapContent) => ({
    ...areaViewMap,
    polygons: areaViewMap.polygons.map((poly) => ({
      ...poly,
      label: getLabel(poly),
      onClick: async () => {
        await updateTriggerBuilding(true)
      },
      onComplete: async () => {
        const blockId = building?.blockOrders?.includes(poly.groupId)
          ? poly.groupId
          : building?.blockOrders?.[0]
        await firebaseControlQuery.update({
          [`building.activeBlock`]: blockId,
        })
      },
      markerColour: projectIdentity?.markerColourSettings?.area,
    })),
  })

  const redirectToLevelView = () => {
    const { maps } = interactivePlanQueryResult
    const { blockOrders } = buildingQueryResult

    const blockSource =
      blockOrders?.length > 1 ? blockOrders : Object.keys(maps?.blocks || {})
    const firstBlockKey = blockSource.length > 1 ? blockSource[0] : ''

    firebaseControlQuery.update({
      [`building.activeBlock`]: firstBlockKey,
    })
    firebaseControlQuery.updateRoute('level-view')
  }

  const area = useMemo(() => {
    if (interactivePlanQueryResult?.maps.areaView?.image) {
      return setInteractiveAction(interactivePlanQueryResult?.maps.areaView)
    }
    return {
      image: '',
      polygons: [],
    }
  }, [interactivePlanQueryResult?.maps])

  const shouldRedirectToLevelView = useMemo(() => {
    const { maps, isLoaded } = interactivePlanQueryResult
    const { isLoaded: isBuildingLoaded } = buildingQueryResult

    return (
      isLoaded &&
      isBuildingLoaded &&
      !maps?.areaView?.image &&
      Object.keys(maps?.blocks || {}).length > 0
    )
  }, [interactivePlanQueryResult.isLoaded, buildingQueryResult.isLoaded])

  useLayoutEffect(() => {
    if (shouldRedirectToLevelView) {
      redirectToLevelView()
    }
  }, [shouldRedirectToLevelView, location])

  useEffect(() => {
    if (
      !interactivePlan.areaView?.image &&
      interactivePlanQueryResult.maps.areaView
    ) {
      setInteractivePlan(interactivePlanQueryResult.maps)
    }
  }, [interactivePlanQueryResult, interactivePlan])

  const sortedLevels = useMemo(
    () =>
      [...buildingQueryResult.levels].sort(
        (a, b) => (a?.height || 0) - (b?.height || 0)
      ),
    [buildingQueryResult.levels]
  )

  useEffect(() => {
    const { levels, blockOrders } = buildingQueryResult

    if (building.levels.length === 0 && levels.length > 0) {
      setBuilding((prev) => ({
        ...prev,
        levels: sortedLevels,
        blockOrders,
      }))
    }
  }, [buildingQueryResult, building])

  useEffect(() => {
    const themeFromStorage = JSON.parse(
      localStorage.getItem('themeObject') || '{}'
    )

    if (themeFromStorage) {
      setTheme(themeFromStorage)
    }
  }, [])

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      areaView: { triggerBuilding },
      building: { activeLevel, activeUnit },
    } = session

    if (activeLevel || activeUnit) {
      resetBuildingMap().catch((error) =>
        console.error('Something went wrong while resetting building ', error)
      )
    }

    if (triggerBuilding) {
      setTimeout(async () => {
        await updateTriggerBuilding(false)
        await resetBuildingView()
        await firebaseControlQuery.updateRoute('level-view')
      }, 1000)
    }
  }, [session])

  return (
    <Container>
      <DataHandler
        payloads={{
          ...interactivePlanQueryResult,
          data: interactivePlan?.areaView?.image,
          status: apiStatus,
        }}
        showDefaultNotFound={!shouldRedirectToLevelView}
        skeletonFrame={<AreaViewApartmentsSkeleton />}
      >
        {area.polygons.length > 0 ? (
          <>
            <ImageHandler
              key={area.image}
              url={area.image}
              type="new"
              className="image-blur background-cover-no-scroll absolute inset-0 z-2"
              noSpliceUrl
              showFallbackImage={false}
              preferCache
              bgProps={{
                gradiant: 0.5,
              }}
            />
            <div className="absolute inset-0 z-3">
              <div
                key="area-canvas"
                data-testid="area-canvas"
                ref={canvasContainerRef}
                className={`relative flex h-full w-full items-center justify-center overflow-hidden ${
                  isCanvasImageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <CanvasInteractive
                  ref={canvasRef}
                  id="area-canvas"
                  canvasData={area}
                  parentRef={canvasContainerRef}
                  theme={{
                    brandColour:
                      theme?.mainColour || themeData.mainColour || '',
                    font: theme?.font || themeData.font || '',
                  }}
                  adjustCanvasSizeWithContainer
                  setImageLoaded={setImageLoaded}
                />
              </div>
            </div>
          </>
        ) : (
          <AreaViewApartmentsSkeleton />
        )}
        <ProjectSwitchToggle
          className="absolute bottom-5 left-5 z-3"
          showOverlayOnProcessing
        />
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({ firestore: { session }, projectIdentity }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
  })
)(AreaViewApartments)
